import { isTelegramWebApp } from './platform';

export const PENDING_REFERRAL_KEY = 'cozzysim_pending_ref';

const BOT_USERNAME = import.meta.env.VITE_BOT_USERNAME || 'cozzysim_bot';
const MINI_APP_SHORT_NAME = import.meta.env.VITE_MINI_APP_SHORT_NAME || 'app';

/** Telegram `startapp` allows A–Z, a–z, 0–9, `_`, `-`, max 64 chars. */
const STARTAPP_RE = /^[A-Za-z0-9_-]{1,64}$/;

export function sanitizeReferralCode(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const code = String(raw).trim();
  if (!STARTAPP_RE.test(code)) return null;
  return code.toUpperCase();
}

export function getPendingReferralCode(): string | null {
  try {
    return sanitizeReferralCode(localStorage.getItem(PENDING_REFERRAL_KEY));
  } catch {
    return null;
  }
}

export function storePendingReferralCode(code: string | null) {
  try {
    const sanitized = sanitizeReferralCode(code);
    if (sanitized) localStorage.setItem(PENDING_REFERRAL_KEY, sanitized);
    else localStorage.removeItem(PENDING_REFERRAL_KEY);
  } catch {
    /* ignore */
  }
}

function readQueryParam(source: string, key: string): string | null {
  if (!source) return null;
  try {
    return new URLSearchParams(source).get(key);
  } catch {
    return null;
  }
}

/**
 * Telegram puts `startapp` into Mini App launch data — not into `https://rcozzy.com/?ref=`.
 * Sources, in order:
 *   1. `WebApp.initDataUnsafe.start_param` (after the official SDK parses the hash)
 *   2. signed `initData` field `start_param`
 *   3. `tgWebAppStartParam` on the URL hash / search (available even before the SDK)
 *   4. `start_param` nested inside hashed `tgWebAppData`
 */
export function readTelegramStartParam(): string | null {
  if (typeof window === 'undefined') return null;

  const tg = window.Telegram?.WebApp;
  const fromUnsafe = sanitizeReferralCode(tg?.initDataUnsafe?.start_param);
  if (fromUnsafe) return fromUnsafe;

  const fromInitData = sanitizeReferralCode(readQueryParam(tg?.initData || '', 'start_param'));
  if (fromInitData) return fromInitData;

  const fromSearch = sanitizeReferralCode(readQueryParam(window.location.search.replace(/^\?/, ''), 'tgWebAppStartParam'));
  if (fromSearch) return fromSearch;

  const hash = (window.location.hash || '').replace(/^#/, '');
  const fromHash = sanitizeReferralCode(readQueryParam(hash, 'tgWebAppStartParam'));
  if (fromHash) return fromHash;

  const webAppData = readQueryParam(hash, 'tgWebAppData');
  if (webAppData) {
    const nested = sanitizeReferralCode(readQueryParam(webAppData, 'start_param'));
    if (nested) return nested;
  }

  return null;
}

/**
 * Persist the referral from this Mini App launch. A fresh `start_param` always
 * overwrites a stale pending code. Launch without `startapp` leaves an existing
 * pending code in place (so a later email/Google signup can still consume it).
 */
export function captureReferralCode(): void {
  if (typeof window === 'undefined') return;
  const fromLaunch = readTelegramStartParam();
  if (fromLaunch) storePendingReferralCode(fromLaunch);
}

/** Direct Mini App link — opens `/app` with `startapp`, not the bot `/start` payload. */
export function buildReferralLink(code: string): string {
  const sanitized = sanitizeReferralCode(code) || code;
  return `https://t.me/${BOT_USERNAME}/${MINI_APP_SHORT_NAME}?startapp=${encodeURIComponent(sanitized)}`;
}

/**
 * Shares the Mini App `startapp` deep link. Inside Telegram we use the native
 * forward dialog; elsewhere Web Share, then clipboard.
 */
export async function shareReferralLink(code: string, text: string): Promise<'shared' | 'copied'> {
  const link = buildReferralLink(code);
  const tg = window.Telegram?.WebApp;

  if (isTelegramWebApp() && tg?.openTelegramLink) {
    tg.openTelegramLink(`https://t.me/share/url?url=${encodeURIComponent(link)}&text=${encodeURIComponent(text)}`);
    return 'shared';
  }

  if (typeof navigator !== 'undefined' && navigator.share) {
    try {
      await navigator.share({ text, url: link });
      return 'shared';
    } catch {
      // User cancelled or the platform doesn't actually support it — fall through.
    }
  }

  if (tg?.openTelegramLink) {
    tg.openTelegramLink(`https://t.me/share/url?url=${encodeURIComponent(link)}&text=${encodeURIComponent(text)}`);
    return 'shared';
  }

  try {
    await navigator.clipboard.writeText(link);
  } catch {
    /* ignore */
  }
  return 'copied';
}
